import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { RouteFragment } from "@impero-com/common/routeFragments";
import { useAjaxManager } from "@impero-com/common/utils/ajax";
import { t } from "@impero-com/common/utils/i18n";
import { FormFrame } from "@impero-com/ui";
import { TableRestore } from "@impero-com/ui/domain";
import { Typography } from "antd";
import { Link, Navigate, Route, Routes } from "react-router-dom";

import { ErrorBoundary } from "components/errorBoundary";
import { FlipperStrictMode } from "components/flipperStrictMode";
import { useDashboardRights } from "utils/hooks/useDashboardRights";
import { useOrgUserProfileCookie } from "utils/profileCookie";

import { FeatureGate } from "../components/FeatureGate";
import { FinancialYearOrgEditor } from "../components/financialYear/editor";
import { Setup2FA } from "../components/twoFactor";
import { WebRoute } from "../routes";
import { useFeatureFlag } from "../utils/hooks/useFeatureFlag";
import { useOrgDisplayInfo } from "../utils/queries/useOrgDisplayInfoSuspenseQuery";
import { useUserStatusQuery } from "../utils/queries/useUserStatusQuery";

import { AlreadyLoggedIn } from "./alreadyLoggedIn";
import { Body } from "./body";
import { ControlsBulkAssignPage } from "./control/bulkAssign/page";
import { ControlsBulkImportPage } from "./control/bulkImport/page";
import { ControlProgramCreatePage } from "./control/controlProgram/view/ControlProgramCreatePage";
import { ControlProgramEditPage } from "./control/controlProgram/view/ControlProgramEditPage";
import { ControlProgramDetailPage } from "./control/controlProgram/view/detail/ControlProgramDetailPage";
import { AllControls } from "./control/list/controls/all";
import { HalfYearlyControls } from "./control/list/controls/halfYearly";
import { ManualControls } from "./control/list/controls/manual";
import { MonthlyControls } from "./control/list/controls/monthly";
import { QuarterlyControls } from "./control/list/controls/quarterly";
import { TwoMonthsControls } from "./control/list/controls/twoMonths";
import { WeeklyControls } from "./control/list/controls/weekly";
import { YearlyControls } from "./control/list/controls/yearly";
import { ControlPrograms } from "./control/list/programs";
import { CreateControlPage } from "./control/view/CreateControlView";
import { UpdateControlPage } from "./control/view/UpdateControlView";
import { ControlActivitiesListForControlPage } from "./controlActivities/list/ControlActivitiesListForControlPage";
import { ControlActivitiesListForControlProgramPage } from "./controlActivities/list/ControlActivitiesListForControlProgramPage";
import { ControlActivitiesListPage } from "./controlActivities/list/ControlActivitiesListPage";
import { ControlActivitiesRestore } from "./controlActivities/list/ControlActivitiesRestore";
import { controlEntitiesListRestoreConfig } from "./controlEntities/list/consts";
import { ControlEntitiesListPage } from "./controlEntities/list/ControlEntitiesListPage";
import { ControlEntitiesCreatePage } from "./controlEntities/view/ControlEntitiesCreatePage";
import { ControlEntitiesEditPage } from "./controlEntities/view/ControlEntitiesEditPage";
import { EntityProgramsListPage } from "./controlEntities/view/list/EntityProgramsListPage";
import { ControlHealthPage } from "./controlHealth/page";
import { ControlPoliciesPage } from "./controlPolicies/ControlPoliciesPage";
import { TestingActivitiesListForProgramPage } from "./controlTesting/testingActivity/list/TestingActivitiesForProgramListPage";
import { TestingControlActivityViewPage } from "./controlTesting/testingActivity/view/TestingActivityViewPage";
import { TestingControlListForProgramPage } from "./controlTesting/testingControl/list/TestingControlListForProgramPage";
import { TestingControlCreatePage } from "./controlTesting/testingControl/view/TestingControlCreatePage";
import { TestingControlEditPage } from "./controlTesting/testingControl/view/TestingControlEditPage";
import { testingProgramListRestoreConfig } from "./controlTesting/testingProgram/list/consts";
import { TestingProgramListPage } from "./controlTesting/testingProgram/list/TestingProgramListPage";
import { TestingProgramCreatePage } from "./controlTesting/testingProgram/view/TestingProgramCreatePage";
import { TestingProgramEditPage } from "./controlTesting/testingProgram/view/TestingProgramEditPage";
import { DashboardOverviewPage } from "./dashboard/page";
import { dashboardPendingActivitiesRestoreConfig } from "./dashboardPendingActivities/consts";
import { DashboardPendingActivitiesPage } from "./dashboardPendingActivities/DashboardPendingActivitiesPage";
import { dashboardPendingActivitiesFromDeprecatedPendingTestsPageUrl } from "./dashboardPendingActivities/urlUtils";
import { EntityRouter } from "./entities/EntityRouter";
import { AsyncEventLog } from "./eventLog";
import { FullPageReload } from "./fullPageReload";
import { HomeRedirect } from "./homeRedirect";
import { PostLoginRedirect } from "./postLoginRedirect";
import { ProfileSettingsPage } from "./profile/ProfileSettingsPage";
import { ReportAccessRightsPage } from "./reports/accessRights/accessRightsPage";
import { ClassificationAccessRightsPage } from "./reports/classification/classificationAccessRightsPage";
import { ClassificationListPage } from "./reports/classification/classificationListPage";
import { ReportsAllPage } from "./reports/list/pages/all";
import { ReportsClassificationPage } from "./reports/list/pages/classification";
import { ReportsDatasheetPage } from "./reports/list/pages/datasheet";
import { ReportsStatusPage } from "./reports/list/pages/status";
import { ReportsWorkloadPage } from "./reports/list/pages/workload";
import { RiskDirectoryView } from "./risks/content";
import { RiskMapImporter } from "./risks/content/mapImporter";
import { StructureEditorView } from "./risks/content/structureView/view";
import { EntityCreator } from "./risks/entity/creator";
import { EntityEditor } from "./risks/entity/editor";
import { SimpleRiskDirectoryCreator } from "./risks/structure/creator";
import { RiskManager } from "./risks/structure/manager";
import { RiskDirectoryEditor } from "./risks/structure/updater";
import {
    NEW_ENTITY,
    NEW_RISK_DIRECTORY,
    RISK_DIRECTORY_STRUCTURE,
    RISK_DIRECTORY_STRUCTURE_PROTO,
    RISK_DIRECTORY_VIEW,
    RISK_MODULE,
} from "./risks/url";
import { SettingsBody } from "./settings/SettingsBody";
import { TagsList } from "./tags";
import { ControlProgramTagsListPage } from "./tags/controlProgramTags/list/ControlProgramTagsListPage";
import { UserControlEntitiesList } from "./user/controlEntities/UserControlEntitiesList";
import { UserControlProgramsList } from "./user/controlPrograms/UserControlProgramsList";
import { UserDirectControlResultsList } from "./user/controlResults/UserDirectControlResultsList";
import { UserGroupControlResultsList } from "./user/controlResults/UserGroupControlResultsList";
import { UserAssignedControlsList } from "./user/controls/UserAssignedControlsList";
import { UserTestingActivitiesList } from "./user/controlTesting/testingActivities/UserTestingActivitiesList";
import { UserTestingControlsList } from "./user/controlTesting/testingControls/UserTestingControlsList";
import { UserTestingProgramList } from "./user/controlTesting/testingPrograms/UserTestingProgramList";
import { UserGroupsList } from "./user/groups/UserGroupsList";
import { UserList } from "./user/list";
import { UserGroupList } from "./user/list/groupList";
import { UserPushReportsRecipientList } from "./user/reports/pushReportsRecipient/UserPushReportsRecipientList";
import { UserPushReportsResponsibleList } from "./user/reports/pushReportsResponsible/UserPushReportsResponsibleList";
import { UserSavedReportsList } from "./user/reports/savedReports/UserSavedReportsList";
import { UserEntitiesList } from "./user/riskManagement/entities/UserEntitiesList";
import { UserRiskDirectoriesList } from "./user/riskManagement/riskDirectories/UserRiskDirectoriesList";
import { UserDetailsPage } from "./user/UserDetailsPage";
import { UserGroupCreator } from "./user/userGroups/create";
import { UserGroupManager } from "./user/userGroups/manager";
import { USER_ID_PATH, USER_SUBPATHS } from "./user/userRoutes";
import { UserViewLayout } from "./user/userViewLayout";
import { UserViewPage } from "./user/UserViewPage";

const { Title } = Typography;

const ORG_PARAM_PATHS = {
    RISK_DIRECTORY_EDITOR: `${RISK_MODULE}/:id${RISK_DIRECTORY_STRUCTURE}`,
    RISK_DIRECTORY_STRUCTURE_PROTO: `${RISK_MODULE + RISK_DIRECTORY_STRUCTURE_PROTO}/:id`,
    RISK_DIRECTORY_IMPORT_RISKMAP: `${RISK_DIRECTORY_VIEW}/:id/import-riskmap`,
    RISK_DIRECTORY_RISK_MANAGER: `${RISK_DIRECTORY_VIEW}/:id/:entityId?`,
    RISK_DIRECTORY_ENTITY_MANAGER: `${WebRoute.RISK_ENTITIES}/:id`,
    USER_GROUPS_NEW: `${WebRoute.USER_GROUPS}/new`,
    USER_GROUPS_VIEW: `${WebRoute.USER_GROUPS}/:id`,
    USER_VIEW: USER_ID_PATH,
    CONTROL_VIEW: `${WebRoute.CONTROLS}/:id`,
    TESTING_PROGRAM_VIEW: `${WebRoute.TESTING_PROGRAMS}/:id`,
    TESTING_PROGRAM_TESTING_CONTROLS_LIST: `${WebRoute.TESTING_PROGRAMS}/:id/testing-controls`,
    TESTING_CONTROL_VIEW: `${WebRoute.TESTING_CONTROLS}/:id`,
    TESTING_CONTROL_ACTIVITY_VIEW: `${WebRoute.TESTING_ACTIVITIES}/:id`,
    TESTING_ACTIVITIES_FOR_TESTING_PROGRAM_LIST: `${WebRoute.TESTING_PROGRAMS}/:id/testing-activities`,
    ENTITY_VIEW: `${WebRoute.ENTITIES}/:id`,
    ENTITY_VIEW_LINKED_PROGRAMS: `${WebRoute.ENTITIES}/:id/control-programs`,
    CONTROL_PROGRAM_VIEW: `${WebRoute.CONTROL_PROGRAMS}/:id`,
    CONTROL_ACTIVITIES_FOR_CONTROL_PROGRAM_LIST: `${WebRoute.CONTROL_PROGRAMS}/:id/${RouteFragment.CONTROL_ACTIVITIES}`,
    CONTROL_ACTIVITIES_FOR_CONTROL_LIST: `${WebRoute.CONTROLS}/:id/${RouteFragment.CONTROL_ACTIVITIES}`,
    CONTROL_PROGRAM_DETAIL: `${WebRoute.CONTROL_PROGRAMS}/:id/view`,
} as const;

export function OrgRoutes() {
    const orgCookie = useOrgDisplayInfo();
    const orgProfileCookie = useOrgUserProfileCookie();
    const ajaxManager = useAjaxManager();
    const { data: userStatus } = useUserStatusQuery();
    const availableActions = userStatus.actions;
    const hasDashboardRights = useDashboardRights();

    const controlTestingEnabled = useFeatureFlag("controlTesting");
    const controlEntitiesEnabled = useFeatureFlag("controlEntities");
    const scimEnabled = useFeatureFlag("scim");
    const mcpEnabled = useFeatureFlag("mcp");

    return (
        <Routes>
            <Route path="/" element={<PostLoginRedirect />} />
            <Route path={WebRoute.HOME} element={<HomeRedirect />} />
            {hasDashboardRights && (
                <Route
                    path={WebRoute.DASHBOARD_V2}
                    element={
                        <Body
                            navId="dashboard"
                            title={t("Dashboard_Title_V2")}
                            routerLinks
                        >
                            <ErrorBoundary routeKey={WebRoute.DASHBOARD_V2}>
                                <DashboardOverviewPage />
                            </ErrorBoundary>
                        </Body>
                    }
                />
            )}
            {hasDashboardRights && (
                <Route
                    path={WebRoute.DASHBOARD}
                    element={
                        <FeatureGate
                            navId="dashboard"
                            featureFlag="newDashboard"
                            title={t("Dashboard_Title")}
                            routeKey={WebRoute.DASHBOARD}
                        >
                            <ControlHealthPage isNewDashboard />
                        </FeatureGate>
                    }
                />
            )}
            {hasDashboardRights && (
                <Route
                    path={WebRoute.CONTROL_HEALTH}
                    element={
                        <FeatureGate
                            navId="dashboard"
                            featureFlag="controlHealthDashboard"
                            title={t("ControlHealth_Title")}
                            routeKey={WebRoute.CONTROL_HEALTH}
                        >
                            <ControlHealthPage isNewDashboard={false} />
                        </FeatureGate>
                    }
                />
            )}
            <Route
                path={WebRoute.DASHBOARD_PENDING_ACTIVITIES}
                element={
                    <Body
                        navId="pendingActions"
                        title={t("Dashboard_PendingActions_Title")}
                        routerLinks
                    >
                        <ErrorBoundary
                            routeKey={WebRoute.DASHBOARD_PENDING_ACTIVITIES}
                        >
                            <TableRestore
                                config={dashboardPendingActivitiesRestoreConfig}
                            >
                                <DashboardPendingActivitiesPage />
                            </TableRestore>
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.CONTROL_ACTIVITIES}
                element={
                    <Body
                        navId="controlActivities"
                        title={t("ControlResults_Title")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.CONTROL_ACTIVITIES}>
                            <ControlActivitiesRestore scopeType="all">
                                <ControlActivitiesListPage />
                            </ControlActivitiesRestore>
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={
                    ORG_PARAM_PATHS.CONTROL_ACTIVITIES_FOR_CONTROL_PROGRAM_LIST
                }
                element={
                    <Body
                        navId="controlActivities"
                        title={t("ControlResults_Title")}
                        routerLinks
                    >
                        <ErrorBoundary
                            routeKey={
                                ORG_PARAM_PATHS.CONTROL_ACTIVITIES_FOR_CONTROL_PROGRAM_LIST
                            }
                        >
                            <ControlActivitiesRestore scopeType="controlProgram">
                                <ControlActivitiesListForControlProgramPage />
                            </ControlActivitiesRestore>
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={ORG_PARAM_PATHS.CONTROL_ACTIVITIES_FOR_CONTROL_LIST}
                element={
                    <Body
                        navId="controlActivities"
                        title={t("ControlResults_Title")}
                        routerLinks
                    >
                        <ErrorBoundary
                            routeKey={
                                ORG_PARAM_PATHS.CONTROL_ACTIVITIES_FOR_CONTROL_LIST
                            }
                        >
                            <ControlActivitiesRestore scopeType="control">
                                <ControlActivitiesListForControlPage />
                            </ControlActivitiesRestore>
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.CONTROLS_BULKIMPORT}
                element={
                    <Body
                        navId="controls"
                        title={t("ControlsBulkImport_Title")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.CONTROLS_BULKIMPORT}>
                            <ControlsBulkImportPage />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.CONTROLS_BULKASSIGN}
                element={
                    <Body
                        navId="controls"
                        title={t("ControlsBulkAssign_Title")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.CONTROLS_BULKASSIGN}>
                            <ControlsBulkAssignPage />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.CONTROLS_NEW}
                element={
                    <FlipperStrictMode boundaryKey="controlView">
                        <Body
                            navId="controls"
                            title={t("CreateControl_CreateTitle")}
                            routerLinks
                        >
                            <ErrorBoundary routeKey={WebRoute.CONTROLS_NEW}>
                                <CreateControlPage />
                            </ErrorBoundary>
                        </Body>
                    </FlipperStrictMode>
                }
            />
            <Route
                path={WebRoute.CONTROL_PROGRAMS_NEW}
                element={
                    <Body
                        navId="controlPrograms"
                        title={t("CreateControlProgram_Title")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.CONTROL_PROGRAMS_NEW}>
                            <ControlProgramCreatePage />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={ORG_PARAM_PATHS.CONTROL_PROGRAM_VIEW}
                element={
                    <Body
                        navId="controlPrograms"
                        title={t("EditControlProgram_Title")}
                        routerLinks
                    >
                        <ErrorBoundary
                            routeKey={ORG_PARAM_PATHS.CONTROL_PROGRAM_VIEW}
                        >
                            <ControlProgramEditPage />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={ORG_PARAM_PATHS.CONTROL_PROGRAM_DETAIL}
                element={
                    <Body
                        navId="controlPrograms"
                        title={t("ViewControlProgram_Title")}
                        routerLinks
                    >
                        <ErrorBoundary
                            routeKey={ORG_PARAM_PATHS.CONTROL_PROGRAM_DETAIL}
                        >
                            <ControlProgramDetailPage />
                        </ErrorBoundary>
                    </Body>
                }
            />

            <Route
                path={WebRoute.SETUP_2FA}
                element={
                    <Body navId={null} title={t("Setup2FA_Title")} routerLinks>
                        <ErrorBoundary routeKey={WebRoute.SETUP_2FA}>
                            <Setup2FA
                                loggedIn={true}
                                challenge={undefined}
                                ajaxManager={ajaxManager}
                                orgCookie={orgCookie}
                                orgProfileCookie={orgProfileCookie}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.REGISTER}
                element={
                    <Body
                        navId={null}
                        title={t("RegisterUser_Title")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.REGISTER}>
                            <AlreadyLoggedIn
                                text={"RegisterUser_YouAreAlreadyLoggedIn"}
                                ajaxManager={ajaxManager}
                                orgProfileCookie={orgProfileCookie}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.RESET_PASSWORD}
                element={
                    <Body
                        navId={null}
                        title={t("ResetPassword_Title")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.RESET_PASSWORD}>
                            <AlreadyLoggedIn
                                text={"ResetPassword_YouAreAlreadyLoggedIn"}
                                ajaxManager={ajaxManager}
                                orgProfileCookie={orgProfileCookie}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.EVENT_LOG}
                element={
                    <SettingsBody
                        section="eventLog"
                        title={t("EventLogPage_Title")}
                        routeKey={WebRoute.EVENT_LOG}
                    >
                        <AsyncEventLog />
                    </SettingsBody>
                }
            />
            <Route
                path={WebRoute.PROFILE}
                element={
                    <SettingsBody
                        section="profile"
                        title={t("Menu_MyProfile")}
                        routeKey={WebRoute.PROFILE}
                    >
                        <ProfileSettingsPage
                            ajaxManager={ajaxManager}
                            orgProfileCookie={orgProfileCookie}
                            orgCookie={orgCookie}
                        />
                    </SettingsBody>
                }
            />
            <Route
                path={NEW_RISK_DIRECTORY}
                element={
                    <Body
                        navId="riskManagement"
                        title={t("RiskDirectory_Creator_Title")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={NEW_RISK_DIRECTORY}>
                            <SimpleRiskDirectoryCreator
                                ajaxManager={ajaxManager}
                                orgProfileCookie={orgProfileCookie}
                                availableActions={availableActions}
                                isCreateTestRiskDirectoryEnabled={useFeatureFlag(
                                    "createTestRiskDirectory"
                                )}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.WONDERLAND}
                element={
                    <Body
                        navId={null}
                        title={"Looking for Alice?"}
                        routerLinks
                        defaultAppBar
                    >
                        <ErrorBoundary routeKey={WebRoute.WONDERLAND}>
                            <FormFrame>
                                <Title style={{ color: "red" }}>
                                    DON'T GIVE THIS URL TO CUSTOMERS!!!{" "}
                                    <span
                                        style={{
                                            fontSize: "0.5em",
                                            color: "black",
                                        }}
                                    >
                                        (please?)
                                    </span>
                                </Title>
                                <Title>Controls</Title>
                                <ul>
                                    <li>
                                        <Link to={WebRoute.CONTROLS_BULKIMPORT}>
                                            {t("ControlsBulkImport_Title")}
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to={WebRoute.CONTROLS_BULKASSIGN}>
                                            {t("ControlsBulkAssign_Title")}
                                        </Link>
                                    </li>
                                </ul>
                                <Title>General organization setup</Title>
                                <ul>
                                    <li>
                                        <Link to={WebRoute.FINANCIAL_YEAR}>
                                            {t("Financial_Year")}
                                        </Link>
                                    </li>
                                </ul>
                            </FormFrame>
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={ORG_PARAM_PATHS.RISK_DIRECTORY_STRUCTURE_PROTO}
                element={
                    <Body
                        navId="riskManagement"
                        title={"Edit Directory structure"}
                        routerLinks
                    >
                        <ErrorBoundary
                            routeKey={
                                ORG_PARAM_PATHS.RISK_DIRECTORY_STRUCTURE_PROTO
                            }
                        >
                            <StructureEditorView />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.FINANCIAL_YEAR}
                element={
                    <Body
                        navId={null}
                        title={t("Financial_Year_Editor_Title")}
                        routerLinks
                        defaultAppBar
                    >
                        <ErrorBoundary routeKey={WebRoute.FINANCIAL_YEAR}>
                            <FinancialYearOrgEditor />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.CONTROL_POLICIES}
                element={
                    <FeatureGate
                        navId="settings"
                        featureFlag="orgSettings"
                        title={t("ControlPolicies_Title")}
                        routeKey={WebRoute.CONTROL_POLICIES}
                    >
                        <ControlPoliciesPage />
                    </FeatureGate>
                }
            />
            <Route
                path={ORG_PARAM_PATHS.RISK_DIRECTORY_EDITOR}
                element={
                    <Body
                        navId="riskManagement"
                        title={t("RiskDirectory_Editor_Title")}
                        routerLinks
                    >
                        <ErrorBoundary
                            routeKey={ORG_PARAM_PATHS.RISK_DIRECTORY_EDITOR}
                        >
                            <RiskDirectoryEditor
                                ajaxManager={ajaxManager}
                                orgProfileCookie={orgProfileCookie}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={ORG_PARAM_PATHS.RISK_DIRECTORY_IMPORT_RISKMAP}
                element={
                    <Body
                        navId="riskManagement"
                        title={t("RiskDirectory_RiskMapImporter")}
                        routerLinks
                    >
                        <ErrorBoundary
                            routeKey={
                                ORG_PARAM_PATHS.RISK_DIRECTORY_IMPORT_RISKMAP
                            }
                        >
                            <RiskMapImporter
                                ajaxManager={ajaxManager}
                                orgProfileCookie={orgProfileCookie}
                                orgCookie={orgCookie}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={ORG_PARAM_PATHS.RISK_DIRECTORY_RISK_MANAGER}
                element={
                    <Body
                        navId="riskManagement"
                        title={t("RiskDirectory_RiskManager")}
                        routerLinks
                    >
                        <ErrorBoundary
                            routeKey={
                                ORG_PARAM_PATHS.RISK_DIRECTORY_RISK_MANAGER
                            }
                        >
                            <RiskDirectoryView
                                ajaxManager={ajaxManager}
                                orgProfileCookie={orgProfileCookie}
                                orgCookie={orgCookie}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={RISK_MODULE}
                element={
                    <Body
                        navId="riskManagement"
                        title={t("RiskDirectory_RiskManager")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={RISK_MODULE}>
                            <RiskManager ajaxManager={ajaxManager} />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={NEW_ENTITY}
                element={
                    <Body
                        navId="riskManagement"
                        title={t("RiskEntities_Create_New_Title")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={NEW_ENTITY}>
                            <EntityCreator />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={ORG_PARAM_PATHS.RISK_DIRECTORY_ENTITY_MANAGER}
                element={
                    <Body
                        navId="riskManagement"
                        title={t("RiskDirectory_EntityManager")}
                        routerLinks
                    >
                        <ErrorBoundary
                            routeKey={
                                ORG_PARAM_PATHS.RISK_DIRECTORY_ENTITY_MANAGER
                            }
                        >
                            <EntityEditor />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.USERS}
                element={
                    <Body
                        navId="userManagement"
                        title={t("UserManagement_ModuleTitle")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.USERS}>
                            <UserList
                                ajaxManager={ajaxManager}
                                controlTestingEnabled={controlTestingEnabled}
                                controlEntitiesEnabled={controlEntitiesEnabled}
                                mcpEnabled={mcpEnabled}
                                scimEnabled={scimEnabled}
                                orgProfileCookie={orgProfileCookie}
                                orgCookie={orgCookie}
                                availableActions={availableActions}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.USER_GROUPS}
                element={
                    <Body
                        navId="userManagement"
                        title={t("UserManagement_ModuleTitle")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.USER_GROUPS}>
                            <UserGroupList
                                ajaxManager={ajaxManager}
                                availableActions={availableActions}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={ORG_PARAM_PATHS.USER_GROUPS_NEW}
                element={
                    <Body
                        navId="userManagement"
                        title={t("UserManagement_ModuleTitle")}
                        routerLinks
                    >
                        <ErrorBoundary
                            routeKey={ORG_PARAM_PATHS.USER_GROUPS_NEW}
                        >
                            <UserGroupCreator
                                ajaxManager={ajaxManager}
                                availableActions={availableActions}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={ORG_PARAM_PATHS.USER_GROUPS_VIEW}
                element={
                    <Body
                        navId="userManagement"
                        title={t("UserGroup_Management_ModuleTitle")}
                        routerLinks
                    >
                        <ErrorBoundary
                            routeKey={ORG_PARAM_PATHS.USER_GROUPS_VIEW}
                        >
                            <UserGroupManager ajaxManager={ajaxManager} />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={ORG_PARAM_PATHS.USER_VIEW}
                element={<UserViewLayout />}
            >
                <Route
                    index
                    element={
                        <UserDetailsPage orgProfileCookie={orgProfileCookie} />
                    }
                />
                <Route element={<UserViewPage />}>
                    <Route
                        path={USER_SUBPATHS.GROUPS.path}
                        element={<UserGroupsList />}
                    />
                    <Route
                        path={USER_SUBPATHS.CONTROLS.path}
                        element={<UserAssignedControlsList />}
                    />
                    <Route
                        path={USER_SUBPATHS.PROGRAMS.path}
                        element={<UserControlProgramsList />}
                    />
                    <Route
                        path={USER_SUBPATHS.DIRECT_CONTROL_RESULTS.path}
                        element={<UserDirectControlResultsList />}
                    />
                    <Route
                        path={USER_SUBPATHS.GROUP_CONTROL_RESULTS.path}
                        element={<UserGroupControlResultsList />}
                    />
                    <Route
                        path={USER_SUBPATHS.TESTING_PROGRAMS.path}
                        element={<UserTestingProgramList />}
                    />

                    <Route
                        path={USER_SUBPATHS.TESTING_ACTIVITIES.path}
                        element={<UserTestingActivitiesList />}
                    />
                    <Route
                        path={USER_SUBPATHS.TESTING_CONTROLS.path}
                        element={<UserTestingControlsList />}
                    />
                    <Route
                        path={USER_SUBPATHS.CUSTOM_REPORTS.path}
                        element={<UserSavedReportsList />}
                    />
                    <Route
                        path={USER_SUBPATHS.PUSH_REPORTS_RESPONSIBLE.path}
                        element={<UserPushReportsResponsibleList />}
                    />
                    <Route
                        path={USER_SUBPATHS.PUSH_REPORTS_RECIPIENT.path}
                        element={<UserPushReportsRecipientList />}
                    />
                    <Route
                        path={USER_SUBPATHS.RISK_DIRECTORIES.path}
                        element={<UserRiskDirectoriesList />}
                    />
                    <Route
                        path={USER_SUBPATHS.RISK_ENTITIES.path}
                        element={<UserEntitiesList />}
                    />
                    {controlEntitiesEnabled && (
                        <Route
                            path={USER_SUBPATHS.CONTROL_ENTITIES.path}
                            element={<UserControlEntitiesList />}
                        />
                    )}
                </Route>
            </Route>
            <Route
                path={WebRoute.CONTROLS}
                element={
                    <Body
                        navId="controlPrograms"
                        title={t("ControlManagement_ModuleTitle")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.CONTROLS}>
                            <ControlPrograms
                                ajaxManager={ajaxManager}
                                orgProfileCookie={orgProfileCookie}
                                orgCookie={orgCookie}
                                availableActions={availableActions}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.CONTROLS_ALL}
                element={
                    <Body
                        navId="controls"
                        title={t("ControlManagement_ModuleTitle")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.CONTROLS_ALL}>
                            <AllControls
                                ajaxManager={ajaxManager}
                                orgProfileCookie={orgProfileCookie}
                                orgCookie={orgCookie}
                                availableActions={availableActions}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.CONTROLS_WEEKLY}
                element={
                    <Body
                        navId="controls"
                        title={t("ControlManagement_ModuleTitle")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.CONTROLS_WEEKLY}>
                            <WeeklyControls
                                ajaxManager={ajaxManager}
                                orgProfileCookie={orgProfileCookie}
                                orgCookie={orgCookie}
                                availableActions={availableActions}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.CONTROLS_MONTHLY}
                element={
                    <Body
                        navId="controls"
                        title={t("ControlManagement_ModuleTitle")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.CONTROLS_MONTHLY}>
                            <MonthlyControls
                                ajaxManager={ajaxManager}
                                orgProfileCookie={orgProfileCookie}
                                orgCookie={orgCookie}
                                availableActions={availableActions}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.CONTROLS_TWO_MONTHS}
                element={
                    <Body
                        navId="controls"
                        title={t("ControlManagement_ModuleTitle")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.CONTROLS_TWO_MONTHS}>
                            <TwoMonthsControls
                                ajaxManager={ajaxManager}
                                orgProfileCookie={orgProfileCookie}
                                orgCookie={orgCookie}
                                availableActions={availableActions}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.CONTROLS_QUARTERLY}
                element={
                    <Body
                        navId="controls"
                        title={t("ControlManagement_ModuleTitle")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.CONTROLS_QUARTERLY}>
                            <QuarterlyControls
                                ajaxManager={ajaxManager}
                                orgProfileCookie={orgProfileCookie}
                                orgCookie={orgCookie}
                                availableActions={availableActions}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.CONTROLS_HALF_YEARLY}
                element={
                    <Body
                        navId="controls"
                        title={t("ControlManagement_ModuleTitle")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.CONTROLS_HALF_YEARLY}>
                            <HalfYearlyControls
                                ajaxManager={ajaxManager}
                                orgProfileCookie={orgProfileCookie}
                                orgCookie={orgCookie}
                                availableActions={availableActions}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.CONTROLS_YEARLY}
                element={
                    <Body
                        navId="controls"
                        title={t("ControlManagement_ModuleTitle")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.CONTROLS_YEARLY}>
                            <YearlyControls
                                ajaxManager={ajaxManager}
                                orgProfileCookie={orgProfileCookie}
                                orgCookie={orgCookie}
                                availableActions={availableActions}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.CONTROLS_MANUAL}
                element={
                    <Body
                        navId="controls"
                        title={t("ControlManagement_ModuleTitle")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.CONTROLS_MANUAL}>
                            <ManualControls
                                ajaxManager={ajaxManager}
                                orgProfileCookie={orgProfileCookie}
                                orgCookie={orgCookie}
                                availableActions={availableActions}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={ORG_PARAM_PATHS.CONTROL_VIEW}
                element={
                    <FlipperStrictMode boundaryKey="controlView">
                        <Body
                            navId="controls"
                            title={t("CreateControl_EditTitle")}
                            routerLinks
                        >
                            <ErrorBoundary
                                routeKey={WebRoute.CONTROLS + "edit"}
                            >
                                <UpdateControlPage />
                            </ErrorBoundary>
                        </Body>
                    </FlipperStrictMode>
                }
            />
            <Route
                path={WebRoute.TESTING_PROGRAMS}
                element={
                    <FeatureGate
                        navId="controlTesting"
                        featureFlag="controlTesting"
                        routeKey={WebRoute.TESTING_PROGRAMS}
                        title={t("ControlTesting_ModuleTitle")}
                    >
                        <TableRestore config={testingProgramListRestoreConfig}>
                            <TestingProgramListPage />
                        </TableRestore>
                    </FeatureGate>
                }
            />
            <Route
                path={WebRoute.TESTING_PROGRAMS_NEW}
                element={
                    <FeatureGate
                        navId="controlTesting"
                        featureFlag="controlTesting"
                        routeKey={WebRoute.TESTING_PROGRAMS_NEW}
                        title={t("ControlTesting_ProgramCreateTitle")}
                    >
                        <TestingProgramCreatePage />
                    </FeatureGate>
                }
            />
            <Route
                path={ORG_PARAM_PATHS.TESTING_PROGRAM_TESTING_CONTROLS_LIST}
                element={
                    <FeatureGate
                        navId="controlTesting"
                        featureFlag="controlTesting"
                        routeKey={
                            ORG_PARAM_PATHS.TESTING_PROGRAM_TESTING_CONTROLS_LIST
                        }
                        title={t("ControlTesting_ProgramViewTitle")}
                    >
                        <TestingControlListForProgramPage />
                    </FeatureGate>
                }
            />
            <Route
                path={ORG_PARAM_PATHS.TESTING_PROGRAM_VIEW}
                element={
                    <FeatureGate
                        navId="controlTesting"
                        featureFlag="controlTesting"
                        routeKey={ORG_PARAM_PATHS.TESTING_PROGRAM_VIEW}
                        title={t("ControlTesting_ProgramEditTitle")}
                    >
                        <TestingProgramEditPage />
                    </FeatureGate>
                }
            />
            <Route
                path={WebRoute.TESTING_CONTROLS_NEW}
                element={
                    <FeatureGate
                        navId="controlTesting"
                        featureFlag="controlTesting"
                        routeKey={WebRoute.TESTING_CONTROLS_NEW}
                        title={t("ControlTesting_ControlCreateTitle")}
                    >
                        <TestingControlCreatePage />
                    </FeatureGate>
                }
            />
            <Route
                path={ORG_PARAM_PATHS.TESTING_CONTROL_VIEW}
                element={
                    <FeatureGate
                        navId="controlTesting"
                        featureFlag="controlTesting"
                        routeKey={ORG_PARAM_PATHS.TESTING_CONTROL_VIEW}
                        title={t("ControlTesting_ControlViewTitle")}
                    >
                        <TestingControlEditPage />
                    </FeatureGate>
                }
            />
            <Route
                path={WebRoute.TESTING_ACTIVITIES_PENDING}
                element={
                    <Navigate
                        to={
                            dashboardPendingActivitiesFromDeprecatedPendingTestsPageUrl
                        }
                        replace
                    />
                }
            />
            <Route
                path={ORG_PARAM_PATHS.TESTING_CONTROL_ACTIVITY_VIEW}
                element={
                    <FeatureGate
                        navId="controlTesting"
                        featureFlag="controlTesting"
                        routeKey={ORG_PARAM_PATHS.TESTING_CONTROL_ACTIVITY_VIEW}
                        title={t("ControlTesting_ControlViewTitle")}
                    >
                        <TestingControlActivityViewPage />
                    </FeatureGate>
                }
            />
            <Route
                path={
                    ORG_PARAM_PATHS.TESTING_ACTIVITIES_FOR_TESTING_PROGRAM_LIST
                }
                element={
                    <FeatureGate
                        navId="controlTesting"
                        featureFlag="controlTesting"
                        routeKey={
                            ORG_PARAM_PATHS.TESTING_ACTIVITIES_FOR_TESTING_PROGRAM_LIST
                        }
                        title={t("ControlTesting_ActivitiesListTitle")}
                    >
                        <TestingActivitiesListForProgramPage />
                    </FeatureGate>
                }
            />
            <Route
                path={WebRoute.ENTITIES}
                element={
                    <FeatureGate
                        navId="settings"
                        featureFlag="controlEntities"
                        routeKey={WebRoute.ENTITIES}
                        title={t("EntityDefinition_Title")}
                    >
                        <TableRestore config={controlEntitiesListRestoreConfig}>
                            <ControlEntitiesListPage />
                        </TableRestore>
                    </FeatureGate>
                }
            />
            <Route
                path={WebRoute.ENTITIES_NEW}
                element={
                    <FeatureGate
                        navId="settings"
                        featureFlag="controlEntities"
                        routeKey={WebRoute.ENTITIES_NEW}
                        title={t("EntityDefinition_Title")}
                    >
                        <ControlEntitiesCreatePage />
                    </FeatureGate>
                }
            />
            <Route
                path={ORG_PARAM_PATHS.ENTITY_VIEW}
                element={
                    <FeatureGate
                        navId="settings"
                        featureFlag="controlEntities"
                        routeKey={ORG_PARAM_PATHS.ENTITY_VIEW}
                        title={t("EntityDefinition_Title")}
                    >
                        <EntityRouter>
                            <ControlEntitiesEditPage />
                        </EntityRouter>
                    </FeatureGate>
                }
            />

            <Route
                path={ORG_PARAM_PATHS.ENTITY_VIEW_LINKED_PROGRAMS}
                element={
                    <FeatureGate
                        navId="settings"
                        featureFlag="controlEntities"
                        routeKey={ORG_PARAM_PATHS.ENTITY_VIEW_LINKED_PROGRAMS}
                        title={t("EntityDefinition_Title")}
                    >
                        <EntityRouter>
                            <EntityProgramsListPage />
                        </EntityRouter>
                    </FeatureGate>
                }
            />
            <Route
                path={WebRoute.TAGS}
                element={
                    <Body
                        navId="settings"
                        title={t("TagManagement_MenuTitle")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.TAGS}>
                            <TagsList
                                ajaxManager={ajaxManager}
                                orgProfileCookie={orgProfileCookie}
                                orgCookie={orgCookie}
                                availableActions={availableActions}
                            />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.TAGS_CONTROL_PROGRAM}
                element={
                    <Body
                        navId="settings"
                        title={t("TagManagement_MenuTitle")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.TAGS_CONTROL_PROGRAM}>
                            <ControlProgramTagsListPage />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.REPORTS}
                element={
                    <Body
                        navId="reports"
                        title={t("ReportDefinition_Title")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.REPORTS}>
                            <ReportsAllPage />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.REPORTS_STATUS}
                element={
                    <Body
                        navId="reports"
                        title={t("ReportDefinition_Title")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.REPORTS_STATUS}>
                            <ReportsStatusPage />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.REPORTS_CLASSIFICATION}
                element={
                    <Body
                        navId="reports"
                        title={t("ReportDefinition_Title")}
                        routerLinks
                    >
                        <ErrorBoundary
                            routeKey={WebRoute.REPORTS_CLASSIFICATION}
                        >
                            <ReportsClassificationPage />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.REPORTS_DATASHEET}
                element={
                    <Body
                        navId="reports"
                        title={t("ReportDefinition_Title")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.REPORTS_DATASHEET}>
                            <ReportsDatasheetPage />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.REPORTS_WORKLOAD}
                element={
                    <Body
                        navId="reports"
                        title={t("ReportDefinition_Title")}
                        routerLinks
                    >
                        <ErrorBoundary routeKey={WebRoute.REPORTS_WORKLOAD}>
                            <ReportsWorkloadPage />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.REPORTS_CLASSIFICATION_CLASSIFICATIONS}
                element={
                    <Body
                        navId="reports"
                        title={t("ReportDefinition_Title")}
                        routerLinks
                    >
                        <ErrorBoundary
                            routeKey={
                                WebRoute.REPORTS_CLASSIFICATION_CLASSIFICATIONS
                            }
                        >
                            <ClassificationListPage />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.REPORTS_ACCESS_RIGHTS}
                element={
                    <Body
                        navId="reports"
                        title={t("Report_EditAccessRights_Title")}
                        routerLinks
                    >
                        <ErrorBoundary
                            routeKey={WebRoute.REPORTS_ACCESS_RIGHTS}
                        >
                            <ReportAccessRightsPage />
                        </ErrorBoundary>
                    </Body>
                }
            />
            <Route
                path={WebRoute.REPORTS_CLASSIFICATION_ACCESS_RIGHTS}
                element={
                    <Body
                        navId="reports"
                        title={t("Classifications_Edit_Title")}
                        routerLinks
                    >
                        <ErrorBoundary
                            routeKey={
                                WebRoute.REPORTS_CLASSIFICATION_ACCESS_RIGHTS
                            }
                        >
                            <ClassificationAccessRightsPage />
                        </ErrorBoundary>
                    </Body>
                }
            />
            {/* Allow react-router to redirect to legacy pages */}
            <Route path="/v1/*" element={<FullPageReload />} />
            <Route
                path="*"
                element={
                    <Body
                        navId={null}
                        title={t("ErrorPage_NotFound")}
                        routerLinks
                    >
                        <LoggedInHTTPError code={404} />
                    </Body>
                }
            />
        </Routes>
    );
}
